import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lightbulb, Users, Clock, TrendingUp, Calendar, Zap, Target } from "lucide-react";
import { format, parseISO, getDay, getHours, differenceInMinutes } from "date-fns";

interface Session {
  id: string;
  date: string;
  cancelled: boolean;
  clients: {
    name: string;
    client_type: string;
  } | null;
}

interface ScheduleRecommendationsProps {
  sessions: Session[];
  weeksBack: number;
  averageRate: number;
  maxHoursPerWeek: number;
}

interface Recommendation {
  id: string;
  type: "opportunity" | "optimization" | "insight";
  title: string;
  description: string;
  impact: string;
  priority: "high" | "medium" | "low";
  icon: typeof Lightbulb;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ScheduleRecommendations = ({ sessions, weeksBack, averageRate, maxHoursPerWeek }: ScheduleRecommendationsProps) => {
  // Generate recommendations based on schedule analysis
  const recommendations = useMemo(() => {
    const recs: Recommendation[] = [];

    if (sessions.length === 0) {
      return [{
        id: "no-data",
        type: "insight" as const,
        title: "Start Building Your Schedule",
        description: "Once you have more session data, we'll provide personalized recommendations to optimize your schedule.",
        impact: "Build your client base to unlock insights",
        priority: "medium" as const,
        icon: Calendar,
      }];
    }

    // Analyze session distribution by day
    const sessionsByDay: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    const sessionsByHour: Record<number, number> = {};
    const clientSessionCounts: Record<string, number> = {};

    for (const session of sessions) {
      const date = parseISO(session.date);
      const day = getDay(date);
      const hour = getHours(date);

      sessionsByDay[day]++;
      sessionsByHour[hour] = (sessionsByHour[hour] || 0) + 1;

      if (session.clients?.name) {
        clientSessionCounts[session.clients.name] = 
          (clientSessionCounts[session.clients.name] || 0) + 1;
      }
    }

    // Find gaps between consecutive sessions
    const sessionsByDate: Record<string, Session[]> = {};
    for (const session of sessions) {
      const dateKey = format(parseISO(session.date), "yyyy-MM-dd");
      if (!sessionsByDate[dateKey]) sessionsByDate[dateKey] = [];
      sessionsByDate[dateKey].push(session);
    }

    let totalGapMinutes = 0;
    let gapCount = 0;

    for (const daySessions of Object.values(sessionsByDate)) {
      const sorted = daySessions.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      for (let i = 0; i < sorted.length - 1; i++) {
        const currentEnd = new Date(new Date(sorted[i].date).getTime() + 60 * 60 * 1000);
        const nextStart = new Date(sorted[i + 1].date);
        const gap = differenceInMinutes(nextStart, currentEnd);
        if (gap >= 30) {
          totalGapMinutes += gap;
          gapCount++;
        }
      }
    }

    // 1. Check for underutilized days
    const avgPerDay = sessions.length / 7;
    const weekdayAvg = (sessionsByDay[1] + sessionsByDay[2] + sessionsByDay[3] + 
                       sessionsByDay[4] + sessionsByDay[5]) / 5;
    
    for (let day = 1; day <= 5; day++) {
      if (sessionsByDay[day] < weekdayAvg * 0.5 && sessionsByDay[day] < weeksBack) {
        recs.push({
          id: `underutilized-${day}`,
          type: "opportunity",
          title: `${DAYS[day]} is Underutilized`,
          description: `You only had ${sessionsByDay[day]} sessions on ${DAYS[day]}s in the past ${weeksBack} weeks. Consider marketing this day to new clients or offering incentives.`,
          impact: `+$${Math.round(averageRate * 4)}/month potential`,
          priority: sessionsByDay[day] === 0 ? "high" : "medium",
          icon: Calendar,
        });
        break; // Only show one underutilized day
      }
    }

    // 2. Check for scheduling concentration
    const peakHour = Object.entries(sessionsByHour)
      .sort((a, b) => b[1] - a[1])[0];
    
    if (peakHour && Number(peakHour[1]) > sessions.length * 0.3) {
      const hourLabel = format(new Date().setHours(Number(peakHour[0]), 0), "h a");
      recs.push({
        id: "peak-concentration",
        type: "insight",
        title: "High Concentration at Peak Hour",
        description: `${Math.round((Number(peakHour[1]) / sessions.length) * 100)}% of your sessions are around ${hourLabel}. This is great for efficiency but limits growth unless you expand hours.`,
        impact: "Consider expanding availability",
        priority: "low",
        icon: Clock,
      });
    }

    // 3. Check for significant gaps
    const avgGapHoursPerWeek = totalGapMinutes / weeksBack / 60;
    if (avgGapHoursPerWeek > 3) {
      const potentialRevenue = Math.round(avgGapHoursPerWeek * 4 * averageRate);
      recs.push({
        id: "reduce-gaps",
        type: "optimization",
        title: "Reduce Schedule Gaps",
        description: `You have an average of ${avgGapHoursPerWeek.toFixed(1)} hours of gaps per week between sessions. Consider moving clients to back-to-back slots to reclaim this time.`,
        impact: `+$${potentialRevenue}/month if filled`,
        priority: "high",
        icon: Zap,
      });
    }

    // 4. Client diversity check
    const uniqueClients = Object.keys(clientSessionCounts).length;
    const topClient = Object.entries(clientSessionCounts)
      .sort((a, b) => b[1] - a[1])[0];
    
    if (topClient && Number(topClient[1]) > sessions.length * 0.25) {
      recs.push({
        id: "client-concentration",
        type: "insight",
        title: "High Dependency on Single Client",
        description: `${topClient[0]} accounts for ${Math.round((Number(topClient[1]) / sessions.length) * 100)}% of your sessions. Consider diversifying to reduce risk.`,
        impact: "Reduce business risk",
        priority: "medium",
        icon: Users,
      });
    }

    // 5. Weekend opportunity
    const weekendSessions = sessionsByDay[0] + sessionsByDay[6];
    if (weekendSessions < weeksBack * 0.5) {
      recs.push({
        id: "weekend-opportunity",
        type: "opportunity",
        title: "Weekend Availability",
        description: `You've only had ${weekendSessions} weekend sessions in ${weeksBack} weeks. Many clients prefer weekends - consider offering limited weekend hours.`,
        impact: `+$${Math.round(averageRate * 8)}/month potential`,
        priority: "medium",
        icon: Target,
      });
    }

    // 6. Early morning or evening expansion
    const earlyMorning = Object.entries(sessionsByHour)
      .filter(([h]) => Number(h) < 8)
      .reduce((sum, [_, count]) => sum + count, 0);
    const evening = Object.entries(sessionsByHour)
      .filter(([h]) => Number(h) >= 18)
      .reduce((sum, [_, count]) => sum + count, 0);
    
    if (earlyMorning < weeksBack && sessions.length > weeksBack * 3) {
      recs.push({
        id: "early-morning",
        type: "opportunity",
        title: "Early Morning Slots Available",
        description: "You have few sessions before 8am. Early morning slots are popular with professionals who work 9-5. Consider offering 6-7am sessions.",
        impact: "Attract working professionals",
        priority: "low",
        icon: Clock,
      });
    }

    // 7. General growth insight based on max hours setting
    const sessionsPerWeek = sessions.length / weeksBack;
    const utilizationPercent = (sessionsPerWeek / maxHoursPerWeek) * 100;
    if (utilizationPercent < 80) {
      recs.push({
        id: "growth-potential",
        type: "opportunity",
        title: "Room for Growth",
        description: `You're averaging ${sessionsPerWeek.toFixed(1)} sessions/week (${utilizationPercent.toFixed(0)}% of your ${maxHoursPerWeek}h goal). There's capacity for growth. Focus on marketing and lead conversion.`,
        impact: `Current: $${Math.round(sessionsPerWeek * averageRate * 4)}/month • Potential at 100%: $${Math.round(maxHoursPerWeek * averageRate * 4)}/month`,
        priority: utilizationPercent < 50 ? "high" : "medium",
        icon: TrendingUp,
      });
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return recs.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [sessions, weeksBack, averageRate]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "opportunity": return "text-success bg-success/10";
      case "optimization": return "text-warning bg-warning/10";
      case "insight": return "text-info bg-info/10";
      default: return "text-muted-foreground bg-muted";
    }
  };

  return (
    <div className="space-y-6">
      {/* Intro Card */}
      <Card className="glass-card border-0 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">AI-Powered Recommendations</h3>
              <p className="text-muted-foreground mt-1">
                Based on your schedule patterns from the last {weeksBack} weeks, here are personalized 
                suggestions to optimize your time and increase revenue.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations List */}
      <div className="space-y-4">
        {recommendations.map((rec) => {
          const Icon = rec.icon;
          return (
            <Card key={rec.id} className="glass-card border-0">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getTypeColor(rec.type)}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{rec.title}</h4>
                      <Badge variant={getPriorityColor(rec.priority)} className="text-xs">
                        {rec.priority} priority
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">
                        {rec.type}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm">
                      {rec.description}
                    </p>
                    <p className="text-sm font-medium text-primary mt-2">
                      {rec.impact}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* New Client Fit Teaser */}
      <Card className="glass-card border-0 border-dashed border-2">
        <CardContent className="pt-6">
          <div className="text-center py-4">
            <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <h4 className="font-semibold">New Client Fit Analysis</h4>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Coming soon: Enter a prospective client's available times and see how well they'd 
              fill your schedule gaps with a compatibility score.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduleRecommendations;
